'use client';

import { usePathname } from 'next/navigation';
import { usePhrasesStore } from '@/store/phrasesStore';
import { useModalStore } from '@/store/modalStore';
import { shallow } from 'zustand/shallow';
import { useEffect } from 'react';
import { useHasHydrated } from '../useHasHydrated';
import { Subpaths } from '@/types/enums/paths';
import { getBrowserCookieValue } from '@/utils/cookie/cookie';
import { config } from '@/config';

export const useLocale = () => {
    const currentPathname = usePathname();
    const { basePath, locales, defaultLocale, localeCookieName } = config;

    const isBrowser = typeof window !== 'undefined';
    const cookie = isBrowser ? document.cookie : null;

    // Get locale from cookie
    if (cookie) {
        const localeCookie = getBrowserCookieValue(cookie, localeCookieName);
        if (localeCookie && locales.includes(localeCookie)) {
            return { locales, locale: localeCookie };
        }
    }

    // Get locale from pathname (server-side)
    const base = isBrowser ? basePath.replace(/\/$/, '') : '';
    const locale = locales.find(
        (locale) =>
            currentPathname === `${base}/${locale}` ||
            currentPathname?.startsWith(`${base}/${locale}/`),
    );
    return {
        locales,
        locale: locale?.trim() || defaultLocale,
    };
};

export const useSetDefaultLocale = () => {
    const pathname = usePathname();
    const { locale, locales } = useLocale();
    const { locale: storedLocale } = usePhrasesStore();
    const { updateModal } = useModalStore(
        (s) => ({
            updateModal: s.updateModal,
        }),
        shallow,
    );
    const hasHydrated = useHasHydrated();

    useEffect(() => {
        if (!hasHydrated) return; // Required to know that phrasesStore has updated with localstorage
        if (pathname.includes(Subpaths.SurveyTimeout)) return; // survey-timeout page needs to change locale without showing modal
        if (storedLocale.hasBeenChecked) return;
        const defaultLocale = getDefaultBrowserLocale(locale, locales);
        if (defaultLocale === locale) return;

        updateModal('localeConfirm', {
            isOpen: true,
            details: { selectedLocale: defaultLocale, isLoggedIn: false },
        });
    }, [hasHydrated]); // Dependency array left mostly empty as it only needs to run once on load
};

const getDefaultBrowserLocale = (locale: string | undefined, locales: string[] | undefined) => {
    const browserLocale = (
        (navigator.languages && navigator.languages.length && navigator.languages[0].slice(0, 5)) ||
        navigator.language.slice(0, 5) ||
        'en'
    ).toLowerCase();

    if ((locales || []).some((l) => l.toLowerCase() === browserLocale)) {
        const localeFromBrowserLocale = locales?.find((l) => l.toLowerCase() === browserLocale);
        return localeFromBrowserLocale || locale;
    }

    return locale;
};
